import { Button } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

import { EMPTY_VALUE } from '../../common/constants';
import { formatCurrency } from '../../common/helpers';
import { Select } from '../../common/interfaces';
import { Order, OrderStatus } from './Orders.interfaces';

// export const transformOrdersToTableFormat = (orders: Order[]): Order[] => {
// }

export const getCurrencyName = (
  currency: string,
  currencies: Select[],
): string => {
  if (currency && currencies.length !== 0) {
    return (
      currencies.find((item) => item.value === currency)?.label || EMPTY_VALUE
    );
  } else {
    return EMPTY_VALUE;
  }
};

function formatCurrPair(str: string): string {
  return str.replace(/_/g, ' - ');
}

export const getOrderColumns = (
  currencies: Select[],
  virtualAssets: Select[],
  onDownloadPDF: (orderId: string) => Promise<void>,
): ColumnsType<Order> => {
  return [
    {
      title: '№',
      dataIndex: 'number',
      key: 'number',
      render: (_, order) => order?.number || EMPTY_VALUE,
    },

    {
      title: 'Дата поручения',
      dataIndex: 'date',
      key: 'date',
      render: (_, order) =>
        order.date ? dayjs(order.date).format('HH:mm DD.MM.YYYY') : EMPTY_VALUE,
      width: 100,
    },

    {
      title: 'Тип',
      dataIndex: 'orderType',
      key: 'orderType',
    },

    {
      title: 'Актив',
      dataIndex: 'assetType',
      key: 'assetType',
      width: 100,
    },

    {
      title: 'Сумма',
      dataIndex: 'sum',
      key: 'sum',
      render: (_, order) =>
        Number(order?.sum) !== 0
          ? formatCurrency(Number(order.sum))
          : EMPTY_VALUE,
    },

    {
      title: 'Кол-во',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (_, order) => order?.quantity || EMPTY_VALUE,
    },

    {
      title: 'Курс',
      dataIndex: 'curs',
      key: 'curs',
      render: (_, order) =>
        order?.curs && order?.status === OrderStatus.SUCCESS
          ? order?.curs
          : 'Формируется',
    },

    {
      title: 'Валюта',
      dataIndex: 'currencyId',
      key: 'currencyId',
      render: (_, order) =>
        order?.currencyId
          ? getCurrencyName(order.currencyId, currencies)
          : order?.corrCurrencyId
            ? getCurrencyName(order.corrCurrencyId, currencies)
            : EMPTY_VALUE,
    },

    {
      title: 'Валютная пара',
      dataIndex: 'currPair',
      key: 'currPair',
      render: (_, order) =>
        order?.currPair ? formatCurrPair(order.currPair) : EMPTY_VALUE,
    },

    {
      title: 'Ценная бумага',
      dataIndex: 'shareName',
      key: 'shareName',
      render: (_, order) => order?.shareName || EMPTY_VALUE,
    },

    {
      title: 'Вирт. актив',
      dataIndex: 'virtualAssetId',
      key: 'virtualAssetId',
      render: (_, order) =>
        order?.virtualAssetId
          ? getCurrencyName(order.virtualAssetId, virtualAssets)
          : EMPTY_VALUE,
    },

    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 100,
    },

    {
      title: 'Экспорт PDF',
      dataIndex: 'fileId',
      key: 'fileId',
      render: (_, order) =>
        order?.fileId ? (
          <Button
            onClick={(event) => {
              event.stopPropagation();
              // window.open(order.pdfLink, '_blank');
              if (order.fileId) {
                onDownloadPDF(order.fileId);
              }
            }}
            variant="outlined"
            color="primary"
          >
            Скачать
          </Button>
        ) : (
          EMPTY_VALUE
        ),
    },
  ];
};

export const omitDeep = (obj: any): any => {
  Object.keys(obj).forEach((key) => {
    if (obj[key] && typeof obj[key] === 'object' && key !== 'period') {
      omitDeep(obj[key]); // Рекурсивная очистка для вложенных объектов.
    } else if (obj[key] === undefined || obj[key] === null) {
      delete obj[key]; // Удаляем ключ, если его значение неподдерживаемо.
    } else if (key === 'period' && !obj[key][0]) {
      delete obj[key];
    }
  });

  return obj; // Возвращаем очищенный объект.
};

// Массив с приоритетами ключей

export function getNameFromKeys(key: string): string {
  switch (key) {
    case 'orderType':
      return 'Тип заявки';
    case 'assetType':
      return 'Тип актива';
    case 'date':
      return 'Дата';
    case 'sum':
      return 'Сумма';
    case 'currencyId':
      return 'Валюта';
    case 'corrCurrencyId':
      return 'Валюта покупки';
    case 'comment':
      return 'Комментарий';
    case 'status':
      return 'Статус';
    case 'depositoryName':
      return 'Депозитарий';
    case 'quantity':
      return 'Количество';
    case 'shareName':
      return 'Ценная бумага';
    case 'virtualAssetId':
      return 'Виртуальный актив';
    case 'number':
      return 'Номер';
    case 'depoAccount':
      return 'Счет депо';
    case 'depoAccountName':
      return 'Название счета депо';
    case 'indetifier':
      return 'Идентификатор';
    case 'invoiceSection':
      return 'Раздел счета';
    case 'numberAndDateAgreement':
      return 'Номер и дата договора';
    case 'orderId':
      return 'ID заявки';
    case 'personalAccountNumber':
      return 'Номер лицевого счета';
    case 'recipientBank':
      return 'Банк';
    default:
      return key;
  }
}
// TODO: вывести все хэлперы перевода сум в один класс
export const formatSum = (num: string): string => {
  if (!num) return '';
  const parts = parseFloat(
    num.replace(/\s/g, '').replace(',', '.').replace(/-/g, ''),
  ) // Убираем пробелы и приводим к числу
    .toFixed(2) // Ограничиваем до 2 знаков после запятой
    .split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' '); // Форматируем тысячные разделители
  return parts.join('.');
};

export const parseStringToNumber = (input: string | number): number => {
  // Удаляем пробелы и конвертируем в число
  const normalized = input.toString().replace(/\s/g, ''); // Убираем пробелы
  const parsed = parseFloat(normalized); // Преобразуем строку в число

  if (isNaN(parsed)) {
    throw new Error(
      `Invalid input: "${input}" cannot be converted to a number.`,
    );
  }

  return parsed;
};
